import invalidateCanvasGraph from '@/hooks/canvas/helpers/invalidateCanvasGraph';
import coerceToRecord from '../helpers/coerceToRecord';
import eventMatchesToolNames from '../helpers/eventMatchesToolNames';
import getStringValue from '../helpers/getStringValue';
import type {
    BuildRefetchCanvasOnItemActionSubscriptionParams,
    ChatEffectSubscription,
} from '../types';

const CANVAS_ITEM_ACTION_TOOL_NAMES = ['run_canvas_item_action', 'canvases.runItemAction'];

// Only actions that mutate persisted item state need a graph refetch. Read-only
// actions (describe/transcribe/getContent/extract) leave the board unchanged.
const MUTATING_CANVAS_ITEM_ACTION_IDS = [
    'setCustomProperties',
    'removeCustomProperties',
    'updateContent',
    'extractBrand',
    'extractText',
];

const buildRefetchCanvasOnItemActionSubscription = (
    _params: BuildRefetchCanvasOnItemActionSubscriptionParams
): ChatEffectSubscription => {
    return {
        id: 'refetch-canvas-on-item-action',
        routePattern: '/agents/:agentId/canvas/:canvasId',
        eventType: 'TOOL_CALL_RESULT',
        filter: ({ event, matchParams }) => {
            if (
                !eventMatchesToolNames({
                    event,
                    toolNames: CANVAS_ITEM_ACTION_TOOL_NAMES,
                })
            ) {
                return false;
            }

            const args = coerceToRecord({ value: event.args });
            const actionId = getStringValue({ value: args.actionId });
            if (!MUTATING_CANVAS_ITEM_ACTION_IDS.includes(actionId)) {
                return false;
            }

            const canvasIdFromArgs = getStringValue({ value: args.canvasId });
            if (!canvasIdFromArgs) {
                return true;
            }

            return canvasIdFromArgs === matchParams.canvasId;
        },
        action: ({ queryClient, matchParams }) => {
            invalidateCanvasGraph({
                queryClient,
                organizationId: matchParams.agentId,
                canvasId: matchParams.canvasId,
            });
        },
    };
};

export default buildRefetchCanvasOnItemActionSubscription;
